import { useState } from 'react';
import { useToast } from '../../../context/ToastContext';
import { useNavigate } from 'react-router-dom';
import { Input } from '../../ui/atoms/input/Input';
import { Button } from '../../ui/atoms/Button/Button';
import { FcGoogle } from 'react-icons/fc';
import { FaApple } from 'react-icons/fa';
import { useHandleGoogleSignIn } from '../../../hooks/useHandleGoogleSignIn';
import { useHandleAppleSignIn } from '../../../hooks/useHandleAppleSignIn';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { auth, db } from '../../../firebase';
import { doc, serverTimestamp, setDoc } from 'firebase/firestore';
import fetchWithAuth from '../../../utils/fetchWithAuth';
import { Capacitor } from '@capacitor/core';

export const SignUpForm = () => {
  const [email, setEmail] = useState('');
  const [validEmail, setValidEmail] = useState(true);
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [passwordMatchError, setPasswordMatchError] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const { showToast } = useToast() as any;
  const navigate = useNavigate();

  const handleGoogleSignIn = useHandleGoogleSignIn();
  const handleAppleSignIn = useHandleAppleSignIn();
  const isIos = Capacitor.getPlatform() === 'ios';

  const checkEmailFormat = (email: string) =>
    setValidEmail(/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email));

  const validatePassword = () => {
    const hasInput = password.length > 0;
    const isInvalid = password.length < 8 || !/\d/.test(password);
    return hasInput && isInvalid;
  };

  const isDisabled =
    !email ||
    !password ||
    !passwordConfirm ||
    !validEmail ||
    !agreedToTerms;

  const handleSubmit = async () => {
    if (password !== passwordConfirm) {
      setPasswordMatchError(true);
      return;
    }
    try {
      const cred = await createUserWithEmailAndPassword(auth, email, password);
      await setDoc(doc(db, 'users', cred.user.uid), {
        email,
        avatarURL: '',
        createdAt: serverTimestamp(),
      });
      await fetchWithAuth(`/api/users`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: cred.user.uid,
          email,
          username: email.split('@')[0],
          name: email.split('@')[0],
          agreedToTerms: true,
        }),
      });
      showToast({
        message: 'Account created! Check your email to verify.',
        type: 'success',
      });
      navigate('/verify');
    } catch (err: any) {
      console.error(err);
      if (err.code === 'auth/email-already-in-use') {
        showToast({ message: 'This email is already in use.', type: 'error' });
      } else if (err.code === 'auth/weak-password') {
        showToast({
          message: 'Password should be at least 8 characters and include a number.',
          type: 'error',
        });
      } else {
        showToast({
          message: 'Something went wrong. Please try again.',
          type: 'error',
        });
      }
    }
  };

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        handleSubmit();
      }}
    >
      {/* ── Main fields spaced at 1rem ── */}
      <div className="space-y-4">
        <Input
          onBlur={() => checkEmailFormat(email)}
          placeholder="Email"
          type="email"
          value={email}
          setValue={(v) => {
            setEmail(v);
            setValidEmail(true);
          }}
        />
        {!validEmail && (
          <div className="text-sm text-red-500">Please use valid email</div>
        )}

        <Input
          placeholder="Password"
          type="password"
          value={password}
          setValue={(v) => {
            setPassword(v);
            setPasswordMatchError(false);
          }}
        />
        {validatePassword() && (
          <div className="text-sm text-gray-500">
            Min 8 characters and one number
          </div>
        )}

        <Input
          placeholder="Confirm Password"
          type="password"
          value={passwordConfirm}
          setValue={(v) => {
            setPasswordConfirm(v);
            setPasswordMatchError(false);
          }}
        />
        {passwordMatchError && (
          <div className="text-sm text-red-500">Passwords don't match</div>
        )}

        <div className="flex items-start space-x-2 text-sm">
          <input
            id="terms"
            type="checkbox"
            checked={agreedToTerms}
            onChange={(e) => setAgreedToTerms(e.target.checked)}
            className="mt-1"
          />
          <label htmlFor="terms" className="text-gray-700">
            I agree to the{' '}
            <a href="/terms" target="_blank" className="underline">
              Terms of Service
            </a>{' '}
            and{' '}
            <a href="/privacy" target="_blank" className="underline">
              Privacy Policy
            </a>
            .
          </label>
        </div>
      </div>

      {/* ── Social buttons super-tight: 1px ── */}
      <div className="mt-4 space-y-2">
        <Button 
          disabled={isDisabled} 
          variant="black" 
          className="bg-brandPurpleDark disabled:bg-brandPurpleLight disabled:text-brandPurpleDark"
          onClick={handleSubmit}>
          Sign Up
        </Button>

        <Button
          variant="transparent"
          onClick={async () => {
            try {
              await handleGoogleSignIn();
            } catch (err: any) {
              showToast({ message: err.message, type: 'error' });
            }
          }}
        >
          <FcGoogle className="text-xl" />
          <span className="ml-2 font-medium text-sm">Sign up with Google</span>
        </Button>

        {isIos && ( 
          <Button
            variant="transparent"
            className="text-white bg-black hover:bg-gray-600"
            onClick={async () => {
              try {
                await handleAppleSignIn();
              } catch (err: any) {
                showToast({ message: err.message, type: 'error' });
              }
            }}
          >
            <FaApple className="text-xl" />
            <span className="ml-2 font-medium text-sm">Sign up with Apple</span>
          </Button>
        )}
      </div>
    </form>
  );
};